import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { signOut, updateProfile, sendEmailVerification, EmailAuthProvider } from 'firebase/auth';
import { onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Persona, PersonaSize } from '@fluentui/react';
import {
  getProducts,
  getStripePayments,
  onCurrentUserSubscriptionUpdate,
  createCheckoutSession,
  Subscription,
  Product
} from '@stripe/firestore-stripe-payments';

import { EditableLabel } from '../components/EditableLabel';
import { auth, app, functions, firestore, getOrganisationByPromoCode, addMemberToOrganisation } from '../firebase';
import { useAuthState } from '../hooks/useAuthState';
import { FullWidthButton } from '../components/FullWidthButton';
import { InvoicesList } from '../components/InvoicesList';
import { PromoSection } from '../components/PromoSection';
import useBonnierPixel from '../hooks/useBonnierPixel';
import { Button, CloseButton, Container, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { UserInfo } from '../interfaces';

export const HomePage = ({ promo }: { promo?: boolean }) => {
  const { t, i18n } = useTranslation();
  const alert = useAlert();
  const { user, loading, refresh } = useAuthState();
  const location = useLocation();
  const { userRoles } = useAuthState();
  let [searchParams, setSearchParams] = useSearchParams();

  const [displayName, setDisplayName] = useState<string>('');

  const [customerSubscriptions, setCustomerSubscriptions] = useState<Subscription[]>([]);
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState<boolean>(true);
  const [stripeError, setStripeError] = useState<string>();
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [renderPromo, setRenderPromo] = useState<number>(0);
  const [promoCode, setPromoCode] = useState<string>('');
  const [showPromoCodeModal, setShowPromoCodeModal] = useState<boolean>(false);
  const [promoCodeError, setPromoCodeError] = useState<boolean>(false);
  const [loadingOrg, setLoadingOrg] = useState<boolean>(false);
  const [userData, setUserData] = useState<UserInfo>();

  const payments = getStripePayments(app, {
    customersCollection: 'customers',
    productsCollection: 'products'
  });

  useEffect(() => {
    if (searchParams.get('isNewAccount')) {
      setShowPromoCodeModal(true);
    }
  }, [searchParams]);

  useEffect(() => {
    const unsubscribe = onCurrentUserSubscriptionUpdate(
      payments,
      (snapshot) => {
        setCustomerSubscriptions(snapshot.subscriptions);
        setIsLoadingSubscriptions(false);
      },
      (error) => {
        setCustomerSubscriptions([]);
        setStripeError(error.code);
        setIsLoadingSubscriptions(false);
      }
    );

    getProducts(payments).then(setProducts);

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) {
      const unsubscribe = onSnapshot(
        doc(firestore, 'userData', user.uid),
        (snapshot) => {
          if (snapshot.exists()) {
            setUserData(snapshot.data() ? (snapshot.data() as UserInfo) : undefined);
            setDisplayName(snapshot.data()?.personalInfo?.name ?? '');
          } else {
            setDisplayName(user?.displayName ?? '');
          }
        },
        () => {
          alert.error(t('unknown-error'));
        }
      );
      return () => unsubscribe();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user && location.search.match(/redirect=stripe/)) {
      createSubscription();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, user]);

  useBonnierPixel('registered', user != null && user.emailVerified === false);

  const createSubscription = () => {
    if (!user?.emailVerified) {
      alert.info(t('stripe.verify-email-before-subscribe'));
      return;
    }

    setIsRedirecting(true);
    // const products = await getProducts(payments, {
    //     includePrices: true,
    //     activeOnly: true
    // });
    // const product = products.find(p => p.id === "prod_L1Va4IsjHKrpyA");
    // if (!product || product.prices.length === 0) {
    //     setIsRedirecting(false);
    //     setStripeError(t("stripe.no-products"));
    //     return;
    // }

    const promo = localStorage.getItem('promo') || '';

    createCheckoutSession(payments, {
      price: 'price_1NbNqrCqknnKgjUYgjY4Z1TR',
      allow_promotion_codes: true,
      success_url: window.location.href.replace(window.location.search, '').replace(/\/$/, '') + '/success',
      cancel_url: window.location.href.replace(window.location.search, ''),
      promotion_code: promo
    })
      .then((session) => {
        window.location.assign(session.url);
      })
      .catch((err) => {
        console.error(err);
        alert.error(t('stripe.possible-promo-code-error'));
        setIsRedirecting(false);
      });
  };

  const nameChanged = (name: string) => {
    if (user) {
      updateProfile(user, { displayName: name })
        .then(() => {
          setDisplayName(name);
        })
        .catch(() => {
          alert.error(t('auth.error-update-profile'));
        });
      updateDoc(doc(firestore, 'userData', user.uid), {
        'personalInfo.name': name
      }).then(() => {
        setDisplayName(name);
      });
    }
  };

  const doSendEmailVerification = () => {
    if (user) {
      sendEmailVerification(user, {
        url: window.location.href
      })
        .then(() => {
          alert.success(t('auth.email-verification-sent'));
        })
        .catch(() => {
          alert.error(t('auth.error-send-email-verification'));
        });
    }
  };

  const navigateToStripePortal = async () => {
    setIsRedirecting(true);
    try {
      const functionRef = httpsCallable(functions, 'ext-firestore-stripe-payments-createPortalLink');
      const { data } = await functionRef({ returnUrl: window.location.href });
      window.location.assign((data as any).url);
    } catch (_) {
      setIsRedirecting(false);
      alert.error(t('stripe.error'));
    }
  };

  const clearPromo = () => {
    localStorage.removeItem('promo');
    setRenderPromo(renderPromo + 1);
  };

  const subscriptionIsActive = (subscription: Subscription) =>
    subscription.status === 'active' || subscription.status === 'trialing';

  const showPasswordRelated = user && user.providerData.find((p) => p.providerId === EmailAuthProvider.PROVIDER_ID);
  const hasActiveSubscription = customerSubscriptions.find(subscriptionIsActive);

  if (location.search.match(/redirect=stripe/) && isRedirecting) {
    return (
      <p>
        {t('stripe.loading')}
        <i className='fa-spin fa-spinner fas ms-2' />
      </p>
    );
  }

  const addToOrganisation = async () => {
    if (!user || !user.email) {
      return;
    }
    setLoadingOrg(true);
    try {
      const _organisation = await getOrganisationByPromoCode(promoCode);
      if (!_organisation) {
        setPromoCodeError(true);
        toast.error(t('registerpage.promo-code-error'));
      }
      if (_organisation) {
        const _seats = _organisation.seats;
        const _usersCount = _organisation.members.length + _organisation.admins.length;
        if (_usersCount >= _seats) {
          toast.error(
            <div>
              <p>{_organisation.name}</p>
              <p>{t('homepage.redeem-promo-seats-error')}</p>
            </div>,
            { autoClose: 5000 }
          );
          //toast.error(`${t('homepage.redeem-promo-seats-error')} ${_organisation.name}`, { autoClose: 5000 });
          return;
        }
        await addMemberToOrganisation(user.email, _organisation.id);
        toast.success(`${t('homepage.member-add-success')} ${_organisation.name}`);
        refresh();
        hidePromoModal();
      }
    } catch (e) {
      console.log('addToOrganisation by promoCode failed:', e);
      toast.error(t('homepage.unknown-error'));
    } finally {
      setLoadingOrg(false);
    }
  };

  const hidePromoModal = () => {
    setPromoCode('');
    if (searchParams.has('isNewAccount')) {
      searchParams.delete('isNewAccount');
      setSearchParams(searchParams);
    }
    setShowPromoCodeModal(false);
  };

  return (
    <>
      <Container>
        <h1 className='h3 mb-3'>{t('homepage.title')}</h1>

        {promo && <PromoSection promo={promo} />}

        {user?.emailVerified && (
          <div className='d-flex mb-3'>
            <Persona
              text={displayName}
              size={PersonaSize.size48}
              imageUrl={user?.photoURL || undefined}
              imageAlt={displayName}
            />
            <EditableLabel
              className='align-self-center flex-grow-1 ms-3 text-start text-truncate'
              value={displayName}
              onChange={nameChanged}
              isLoading={loading}
            />
          </div>
        )}

        {user && !user.emailVerified && (
          <div className='alert alert-success d-block'>
            <div
              dangerouslySetInnerHTML={{
                __html: t('auth.email-not-verified', { email: user.email })
              }}
            />
            <button className='btn btn-link btn-sm' onClick={doSendEmailVerification}>
              {t('auth.verify-email')}
            </button>
          </div>
        )}

        {!isLoadingSubscriptions && stripeError && (
          <div className='alert alert-danger justify-content-start my-3'>
            <i className='fa-exclamation-triangle fas me-2' />
            {t('stripe.error')}
          </div>
        )}

        {user?.emailVerified &&
        !isLoadingSubscriptions &&
        (userRoles.adminIn.length > 0 || userRoles.memberIn.length > 0) ? (
          <div className='alert alert-warning my-3 py-1'>
            <i className='fa-exclamation-triangle fas me-2' />
            {t('homepage.org-subscription')}:{' '}
            {userRoles.adminIn.map((org, i) => (
              <strong key={i}>{i > 0 ? ` & ${org.name}` : org.name}</strong>
            ))}
            {userRoles.memberIn.map((org, i) => (
              <strong key={i}>{userRoles.adminIn.length > 0 || i > 0 ? ` & ${org.name}` : org.name}</strong>
            ))}
          </div>
        ) : userData && (userData.appleSubscription || userData?.googleSubscription) ? (
          <div className='alert alert-warning my-3 py-1'>
            <i className='fa-exclamation-triangle fas me-2' />
            {t('homepage.iap-subscription')}
          </div>
        ) : user?.emailVerified && !isLoadingSubscriptions && !hasActiveSubscription ? (
          <div className='alert alert-warning my-3 py-1'>
            <i className='fa-exclamation-triangle fas me-2' />
            {t('stripe.no-subscription')}

            <div>
              <button className='btn btn-link' onClick={createSubscription} disabled={isRedirecting}>
                {t('stripe.subscribe')}
                {isRedirecting && <i className='fa-spin fa-spinner fas ms-2' />}
              </button>
            </div>
          </div>
        ) : null}

        {user?.emailVerified && (
          <>
            <h3 className='d-flex h5 py-3 text-truncate'>{t('homepage.general')}</h3>
            <div>
              <label className='mb-3 text-start text-truncate w-100'>
                {t('email')}: {user?.email}
              </label>
            </div>

            {showPasswordRelated && (
              <>
                <div>
                  <label className='mb-3 text-start text-truncate w-100'>{t('password')}: ********</label>
                </div>
                <Link to='/email/change' className='text-decoration-none'>
                  <FullWidthButton>{t('auth.change-email')}</FullWidthButton>
                </Link>
                <Link to='/password/change' className='text-decoration-none'>
                  <FullWidthButton>{t('auth.change-password')}</FullWidthButton>
                </Link>
                {userRoles.adminIn.length === 0 && userRoles.memberIn.length === 0 && (
                  <FullWidthButton onClick={() => setShowPromoCodeModal(true)}>
                    {t('homepage.redeem-promo-code')}
                  </FullWidthButton>
                )}
              </>
            )}

            {isLoadingSubscriptions && <span className='text-muted'>{t('stripe.loading')}</span>}

            {!isLoadingSubscriptions && customerSubscriptions.length > 0 && (
              <ul className='list-unstyled mb-0'>
                {customerSubscriptions
                  .sort((a, b) => new Date(b.created).valueOf() - new Date(a.created).valueOf())
                  .map((subscription) => (
                    <li key={subscription.id} className='align-items-center d-flex flex-column my-1'>
                      <span className='flex-grow-1 text-start'>
                        {subscriptionIsActive(subscription) ? (
                          <i className='fa-check-circle fas me-2 text-success' />
                        ) : (
                          <i className='fa-minus-circle fas me-2 text-muted' />
                        )}
                        {t('stripe.active-to', {
                          product: products.find((p) => p.id === subscription.product)?.name ?? '',
                          date: new Date(subscription.current_period_end).toLocaleDateString(i18n.language)
                        })}
                      </span>
                      {subscription.status === 'trialing' && (
                        <span className='badge bg-info mt-2'>{t('stripe.trial')}</span>
                      )}
                      <button className='btn btn-link' onClick={navigateToStripePortal} disabled={isRedirecting}>
                        {t('stripe.manage-subscription')}
                        {isRedirecting && <i className='fa-spin fa-spinner fas ms-2' />}
                      </button>
                    </li>
                  ))}
              </ul>
            )}

            {user && <InvoicesList payments={payments} uid={user.uid} subscriptions={customerSubscriptions} />}
          </>
        )}

        <button
          type='reset'
          className='btn btn-outline-secondary mt-2 w-100'
          onClick={() => signOut(auth)}
          disabled={loading}
        >
          {t('homepage.sign-out')}
        </button>

        <Link to='/account/remove' className='text-decoration-none'>
          <button type='reset' className='btn btn-link btn-sm mb-3 mt-4 w-100' disabled={loading}>
            {t('homepage.remove-account')}
          </button>
        </Link>

        {!promo && <PromoSection promo={promo} />}

        {!isNaN(renderPromo) && localStorage.getItem('promo') && (
          <div className='mb-3'>
            <button className='btn btn-link btn-sm text-muted' onClick={clearPromo} disabled={isRedirecting}>
              <i className='fas fa-times me-1' />
              {t('stripe.clear-promo')}
            </button>
          </div>
        )}

        <div className='text-center text-muted mt-3 w-100'>
          <a href='https://enzoway.com/' className='btn btn-link'>
            <i className='fa-arrow-right fas me-2' />
            {t('to-homepage')}
          </a>
        </div>
      </Container>
      <Modal show={showPromoCodeModal} onHide={() => hidePromoModal()}>
        <Modal.Header>
          <div className='d-flex flex-row justify-content-between align-items-center w-100'>
            <Modal.Title>{t('registerpage.promo-code-modal-header')}</Modal.Title>
            <CloseButton onClick={() => hidePromoModal()} style={{ backgroundColor: 'lightgray', marginRight: 2 }} />
          </div>
        </Modal.Header>
        <Modal.Body style={{ width: '100%' }}>
          <div>
            <Form.Group>
              <Form.Label>{t('registerpage.promo-code')} </Form.Label>
              <Form.Control
                type='text'
                placeholder={t('registerpage.promo-code')}
                name={t('registerpage.promo-code')}
                value={promoCode}
                onChange={(e) => {
                  if (promoCodeError) setPromoCodeError(false);
                  setPromoCode(e.target.value);
                }}
                isInvalid={promoCodeError}
                style={{ backgroundColor: 'black', marginBottom: '1rem' }}
              />

              <Form.Control.Feedback type='invalid'>{t('registerpage.promo-code-error')}</Form.Control.Feedback>
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => hidePromoModal()} disabled={loadingOrg}>
            {t('registerpage.skip')}
          </Button>
          <Button variant='primary' onClick={() => addToOrganisation()} disabled={loadingOrg}>
            {t('registerpage.redeem')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
